/**
 * 음성 저장 사용량 — **보이는 값은 콘솔이 만든다** (2026-09-21 확정).
 *
 * 서버는 **바이트 숫자만** 준다. 사람이 읽는 값도 퍼센트도 서버가 안 만든다 — 만들면
 * **단위·반올림 규칙이 서버와 콘솔 두 벌**이 되고, 두 벌이 되면 언젠가 어긋난다.
 *
 * ## ⚠ 단위는 GiB(1024)다 — 「GB」라고 쓰면 서버 값과 어긋난다
 *
 * 한도가 환경변수 `VOICE_CHUNK_LIMIT_GIB` 로 잡히므로 **1024 기준**이 정본이다.
 * 1000 기준으로 적으면 50GiB 를 「53.7GB」로 부르게 되고, **한도 근처에서 「왜 아직 안 막히지」**가 된다.
 *
 * ## ⛔ 모르는 값을 지어내지 않는다
 *
 * 한도가 0 이거나 없으면 **퍼센트를 만들지 않는다**(0 으로 나누면 Infinity 가 화면에 뜬다).
 * 가장 오래된 조각이 없으면 「—」다 — 「오늘」이 아니다.
 */

export const GIB = 1024 ** 3;

/** 바이트 → 「12.0 GiB」. 1024 기준 · 소수 한 자리. */
export function formatGiB(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  return `${(bytes / GIB).toFixed(1)} GiB`;
}

/** 쓴 비율 0~1. 한도를 모르면 **null** — 0 이 아니다(「안 썼다」와 「모른다」는 다르다). */
export function usageRatio(used: number, limit: number): number | null {
  if (!Number.isFinite(used) || !Number.isFinite(limit) || limit <= 0) return null;
  return used / limit;
}

/** 보여 줄 퍼센트(정수). 모르면 null. ⚠ 100% 를 넘겨도 그대로 적는다 — 넘친 것은 넘친 것이다. */
export function usagePercent(used: number, limit: number): number | null {
  const r = usageRatio(used, limit);
  return r === null ? null : Math.round(r * 100);
}

/** 막대 길이(0~100). 한도를 모르면 0 — 막대는 «모름»을 그리지 못한다(그래서 글자가 따로 말한다). */
export function barWidth(used: number, limit: number): number {
  const r = usageRatio(used, limit);
  return r === null ? 0 : Math.min(100, Math.round(r * 100));
}

/**
 * 한 회사의 형편.
 *
 * ⛔ `full` 은 **지금 사람이 녹음을 못 하고 있다**는 뜻이다(`413 VOICE_STORAGE_FULL`).
 *    서버는 오래된 조각을 **지우지 않으므로** 저절로 풀리지 않는다.
 */
export type StorageLevel = 'ok' | 'near' | 'full' | 'unknown';

/** 「곧 참」의 문턱 — 90%. 한도의 10% 는 1시간 회의 약 45회 분량이라 손 쓸 틈이 된다. */
export const NEAR_FULL_RATIO = 0.9;

export function storageLevel(used: number, limit: number): StorageLevel {
  const r = usageRatio(used, limit);
  if (r === null) return 'unknown';
  if (r >= 1) return 'full';
  if (r >= NEAR_FULL_RATIO) return 'near';
  return 'ok';
}

export const STORAGE_LEVEL_LABEL: Record<StorageLevel, string> = {
  ok: '여유',
  near: '곧 참',
  full: '녹음 멈춤',
  unknown: '한도 모름',
};

/**
 * 며칠 치가 쌓였나 — 「무엇부터 볼지」의 실마리.
 * ⚠ 없으면 null 이다. 「0일」이 아니다(오늘 시작한 것과 기록이 없는 것은 다르다).
 */
export function daysSince(iso: string | null, now: Date = new Date()): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const days = Math.floor((now.getTime() - t) / 86_400_000);
  return days < 0 ? 0 : days;
}
